import React, { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import cn from "clsx";

import useBodyScrollability from "@/hooks/useBodyScrollability";

const FilterOptionsContainer: FC<
  HTMLAttributes<HTMLDivElement> & { filterWrapperClassName: string }
> = ({ children, filterWrapperClassName }) => {
  const [openedClass, setOpenedClass] = useState("");
  const filterModalWrapperRef = useRef<HTMLDivElement | null>(null);
  const { disableBodyScroll, enableBodyScroll } = useBodyScrollability();
  const scrollYRef = useRef(0);
  const bodyStyleRef = useRef<{
    position: string;
    top: string;
    width: string;
    height: string;
  } | null>(null);
  const shouldLockBodyScroll =
    filterWrapperClassName?.includes("size_chart_wrapper");

  const ensureElementWithinBounds = ref => {
    const elementRect = ref.current?.getBoundingClientRect();
    if (!elementRect) return;
    const screenRect = {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight
    };

    const checkRightSide =
      elementRect.right > screenRect.right &&
      elementRect.left < screenRect.right;

    const checkLeftSide =
      elementRect.left < screenRect.left && elementRect.right > screenRect.left;

    if (checkRightSide || checkLeftSide) {
      setOpenedClass("open_filter_to_other_side");
    }
  };

  useEffect(() => {
    ensureElementWithinBounds(filterModalWrapperRef);
    if (!shouldLockBodyScroll) return;

    const scrollY = window.scrollY;
    scrollYRef.current = scrollY;
    bodyStyleRef.current = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      height: document.body.style.getPropertyValue("height")
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    disableBodyScroll();
    // With top: -scrollY and height: 100%, the bottom of `body` ends at 100vh - scrollY, leaving a
    // white gap equal to the scroll offset. Extend height so the fixed layer still covers the viewport.
    document.body.style.setProperty(
      "height",
      `calc(100vh + ${scrollY}px)`,
      "important"
    );
    return () => {
      enableBodyScroll();
      if (bodyStyleRef.current) {
        document.body.style.position = bodyStyleRef.current.position;
        document.body.style.top = bodyStyleRef.current.top;
        document.body.style.width = bodyStyleRef.current.width;
        document.body.style.removeProperty("height");
        if (bodyStyleRef.current.height) {
          document.body.style.setProperty(
            "height",
            bodyStyleRef.current.height
          );
        }
      }
      window.scrollTo(0, scrollYRef.current);
    };
  }, [shouldLockBodyScroll, disableBodyScroll, enableBodyScroll]);
  return (
    <div
      ref={filterModalWrapperRef}
      id="web_filter"
      className={cn("filter_content", openedClass, filterWrapperClassName)}
      onClick={e => e.stopPropagation()}
    >
      {children}
    </div>
  );
};

export default FilterOptionsContainer;
